import { Component, OnDestroy, OnInit } from "@angular/core";
import { Store } from "@ngrx/store";
import { Subscription } from "rxjs";
import { distinctUntilChanged } from "rxjs/operators";
import * as _ from "lodash";

import * as fromApp from "src/app/store/app.reducer";
import * as selectors from "src/app/store/app.selectors";
import { resolveSponsorLogoByKey } from "src/app/services/sponsor-logo/sponsor-logo";

/**
 * Renders sponsor logo banner(s) above the cart product rows.
 *
 * A sponsor is shown only when BOTH hold:
 *  - the store whitelists it in `storeInfo.design_settings.cartSponsorLogos`, AND
 *  - at least one product currently in the cart declares that sponsor via
 *    `kiosk_design_settings.sponsorLogo`.
 *
 * The intersection is rendered in the store's whitelist order. The banner hides
 * itself on an empty cart, missing/empty store config, unknown sponsor keys, or
 * an image that fails to load (missing/404). Display-only: it does not touch
 * cart products, final price or the create-order payload.
 */
@Component({
  selector: "app-cart-sponsor-banner",
  templateUrl: "./cart-sponsor-banner.component.html",
  styleUrls: ["./cart-sponsor-banner.component.scss"],
})
export class CartSponsorBannerComponent implements OnInit, OnDestroy {
  // Resolved { key, url } pairs to render, in store whitelist order.
  public sponsorLogos: { key: string; url: string }[] = [];

  private cartSponsorLogos: string[] = []; // store whitelist / priority order
  private cartProducts: any[] = [];
  private failedKeys: string[] = []; // keys whose image failed to load
  private subscriptions: Subscription[] = [];

  constructor(private store: Store<fromApp.AppState>) {}

  ngOnInit() {
    this.subscriptions.push(
      this.store
        .select(selectors.getStoreInfo)
        .pipe(distinctUntilChanged())
        .subscribe((storeInfo: any) => {
          const configured =
            (storeInfo &&
              storeInfo.design_settings &&
              storeInfo.design_settings.cartSponsorLogos) ||
            [];
          if (!_.isEqual(this.cartSponsorLogos, configured)) {
            this.cartSponsorLogos = _.cloneDeep(configured);
            this.computeSponsorLogos();
          }
        }),
    );

    this.subscriptions.push(
      this.store
        .select("cart")
        .pipe(distinctUntilChanged())
        .subscribe((state: any) => {
          const products = (state && state.products) || [];
          if (!_.isEqual(this.cartProducts, products)) {
            this.cartProducts = _.cloneDeep(products);
            this.computeSponsorLogos();
          }
        }),
    );
  }

  private computeSponsorLogos() {
    // Whitelist configured on the store; nothing to show without it.
    if (!_.isArray(this.cartSponsorLogos) || !this.cartSponsorLogos.length) {
      this.sponsorLogos = [];
      return;
    }
    // Distinct sponsor keys present on the products currently in the cart.
    const keysInCart = _.uniq(
      _.compact(
        _.map(
          this.cartProducts,
          (product: any) =>
            product &&
            product.kiosk_design_settings &&
            product.kiosk_design_settings.sponsorLogo,
        ),
      ),
    );
    // Intersect whitelist x cart, keep the store's whitelist order, and resolve
    // to logo URLs (unknown keys / failed images resolve to nothing). Dedupe the
    // whitelist so a repeated key never renders (or track-bys) twice.
    this.sponsorLogos = _.compact(
      _.map(_.uniq(this.cartSponsorLogos), (key: string) => {
        if (!_.includes(keysInCart, key) || _.includes(this.failedKeys, key)) {
          return null;
        }
        const url = resolveSponsorLogoByKey(key);
        return url ? { key, url } : null;
      }),
    );
  }

  /** Hide a sponsor logo whose image failed to load (missing/404). */
  public onImageError(key: string) {
    if (!_.includes(this.failedKeys, key)) {
      this.failedKeys.push(key);
      this.computeSponsorLogos();
    }
  }

  public trackBySponsorKey(index: number, item: { key: string }) {
    return item.key;
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.subscriptions = [];
  }
}
